import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import type { Filter, MeasurementUnit } from '@cvent/venue-search-shared/types';
import ApplyResetWrapper from '@/components/filters/ApplyResetWrapper';
import dynamic from 'next/dynamic';
import type { Locale } from '@/i18n';
import Loader from '@/components/filters/Loader';
import { formatMessage } from '@/utils/translationHelper';
import useFullMap from '@/hooks/useFullMap';

const BrandAndChainFilter = dynamic(() => import('@/components/filters/BrandAndChainFilter'), {
  ssr: false,
  loading: () => <Loader />
});

type Props = {
  searchTerm: string;
  measurementUnit: MeasurementUnit;
  locale: Locale;
  appliedFilters: Filter;
  labels: {
    title: string;
    allFilters: string;
    resetAllFilters: string;
    cancelAllFilters: string;
    applyAllFilters: string;
    clear: string;
    apply: string;
  };
  setSortingLabel?: Dispatch<SetStateAction<string>>;
  setSortingType?: Dispatch<SetStateAction<string>>;
  sortOptions?: { label: string; value: string }[];
  sortType?: string;
  sort?: string;
  setSort?: Dispatch<SetStateAction<string>>;
  setIsRfpBannerVisible: (isRfpBannerVisible: boolean) => void;
  testID?: string;
  isSearchExpanded: boolean;
  setIsSearchExpanded: (v: boolean) => void;
};

export default function BrandAndChainFilterContainer({
  searchTerm,
  measurementUnit,
  locale,
  appliedFilters,
  labels,
  sortOptions,
  sortType,
  sort,
  setSort,
  setSortingType,
  setSortingLabel,
  setIsRfpBannerVisible,
  testID,
  isSearchExpanded,
  setIsSearchExpanded
}: Props) {
  const { title, allFilters, resetAllFilters, cancelAllFilters, applyAllFilters, clear, apply } = labels;
  const [selected, setSelected] = useState<Filter>(appliedFilters);
  const [isOpen, setIsOpen] = useState(false);
  const { brandCityCountry, setBrandCityCountry } = useFullMap();
  const [filterCount, setFilterCount] = useState<number>(0);

  useEffect(() => {
    setSelected(appliedFilters);
    setBrandCityCountry(null);

    // Calculating filter count to be used on surface filter
    let count = appliedFilters.MULTI_FILTER?.BRAND?.length || 0;
    count += appliedFilters.MULTI_FILTER?.CHAIN?.length || 0;
    count += appliedFilters.MULTI_FILTER?.VENUE_NAME?.length || 0;
    setFilterCount(count);
  }, [appliedFilters, setBrandCityCountry]);

  return (
    <ApplyResetWrapper
      venueAutoComplete={{ searchTerm, measurementUnit, locale, brandCityCountry, setBrandCityCountry }}
      setIsSearchExpanded={setIsSearchExpanded}
      isSearchExpanded={isSearchExpanded}
      title={formatMessage(title, locale, {
        count: filterCount
      })}
      selected={selected}
      setSelected={setSelected}
      appliedFilters={appliedFilters}
      filters={['MULTI_FILTER']}
      testID={testID}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      strings={{ allFilters, resetAllFilters, cancelAllFilters, applyAllFilters, clear, apply }}
      showShadow
      setSortingLabel={setSortingLabel}
      setSortingType={setSortingType}
      sortOptions={sortOptions}
      sortType={sortType}
      sort={sort}
      setSort={setSort}
      searchTerm={searchTerm}
      setIsRfpBannerVisible={setIsRfpBannerVisible}
      chainsBrandSurface
      filterCount={filterCount}
    >
      {brandCityCountry ? (
        <BrandAndChainFilter
          options={brandCityCountry.brandAndChainOptions}
          selected={selected}
          setSelected={setSelected}
          showTitle={false}
          testID={testID}
        />
      ) : (
        <Loader />
      )}
    </ApplyResetWrapper>
  );
}
